import { sessionStore } from '@/lib/storage';
import { useMediaQuery } from '@/lib/useMediaQuery';
import { hasFlag } from '@/router/deepLink';

/**
 * Two conditions, either of which makes the desktop metaphor unusable:
 * a screen too narrow for a 640px window, or a touch screen small enough that
 * 6px resize edges and 21px caption buttons cannot be hit.
 */
const NARROW = '(max-width: 767px)';
const COARSE = '(pointer: coarse) and (max-width: 1024px)';

/** Per-tab, so the choice survives `useDeepLinkSync` rewriting `?desktop=1` away. */
export const FORCE_DESKTOP_KEY = 'teonio.forceDesktop';

let forced: boolean | null = null;
let summoned: boolean | null = null;

/**
 * Read once per document. `?desktop=1` is stripped from the address bar by the
 * URL sync as soon as a window is focused, so the flag has to be remembered
 * somewhere: a visitor who tapped "try the desktop version" should not be
 * bounced back to the mobile page by the next re-render.
 *
 * `?ipod=1` clears it, which is the half that was missing. Nothing ever
 * removed the key, so a phone visitor who chose `Dock to PC` could not get
 * back to the iPod for the life of the tab — a one-way door on the only
 * control that was supposed to be reversible.
 */
function forceDesktop(): boolean {
  if (forced === null) {
    forced = !forceIpod() && (hasFlag('desktop') || sessionStore.get(FORCE_DESKTOP_KEY) === '1');
    if (forced) sessionStore.set(FORCE_DESKTOP_KEY, '1');
  }
  return forced;
}

/**
 * The other direction.
 *
 * The iPod is otherwise reachable only from a screen under 768 pixels or a
 * coarse pointer — so on a desktop, the one part of this site that is a whole
 * device rather than a window can be seen only by dragging the browser narrow
 * enough to hide it. `?ipod=1` is the link that does not require that.
 */
function forceIpod(): boolean {
  if (summoned === null) {
    summoned = hasFlag('ipod');
    // Cleared here rather than in `forceDesktop`, which returns before it can
    // when the iPod is summoned — which left the flag doing the visible half
    // of its job and none of the durable half: the device appeared, and the
    // next visit without the flag went straight back to the desktop.
    if (summoned) sessionStore.remove(FORCE_DESKTOP_KEY);
  }
  return summoned;
}

export function useIsMobile(): boolean {
  const narrow = useMediaQuery(NARROW);
  const coarse = useMediaQuery(COARSE);

  // Both queries are subscribed unconditionally so rotation stays reactive
  // even while an override is in force.
  if (forceIpod()) return true;
  if (forceDesktop()) return false;
  return narrow || coarse;
}
